import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { WIDGET_API_ERROR_CHANNEL_NOT_FOUND } from 'widget/consts/apiErrors';
import type { RootStore } from 'widget/stores/RootStore';
import {
  IWidgetCustomization,
  IWidgetSubmitStatus,
  normalizeWidgetCustomization
} from 'widget/entities/config';
import { normalizeWidgetSubmitStatus } from 'widget/entities/config/normalizers';

type privateFields =
  | '_isInitialized'
  | '_isLoading'
  | '_isError'
  | '_customizationConfig'
  | '_submitStatuses';

export class ConfigStore {
  private _rootStore: RootStore;
  private _isInitialized = false;
  private _isLoading = false;
  private _isError = false;
  private _customizationConfig: IWidgetCustomization | null = null;
  private _submitStatuses: IWidgetSubmitStatus | null = null;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeObservable<ConfigStore, privateFields>(this, {
      _isInitialized: observable,
      _isLoading: observable,
      _isError: observable,
      _customizationConfig: observable,
      _submitStatuses: observable,

      isLoading: computed,
      isInitialized: computed,
      isError: computed,
      customizationConfig: computed,
      submitStatuses: computed,

      init: action,
      retry: action
    });
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  get isError(): boolean {
    return this._isError;
  }

  get customizationConfig(): IWidgetCustomization | null {
    return this._customizationConfig;
  }

  get submitStatuses(): IWidgetSubmitStatus | null {
    return this._submitStatuses;
  }

  init = async (): Promise<void> => {
    if (this._isLoading || this._isInitialized) {
      return;
    }

    this._isLoading = true;

    const initToken = this._rootStore.initToken;

    let response = await this._rootStore.apiStore.getWidgetConfig(initToken);

    if (
      response.isError &&
      response.data === WIDGET_API_ERROR_CHANNEL_NOT_FOUND
    ) {
      response = await this._rootStore.apiStore.getWidgetConfig(initToken, {
        omitAuthToken: true
      });
    }

    runInAction(() => {
      this._isLoading = false;

      if (response.isError) {
        this._isError = true;
        return;
      }

      this._isInitialized = true;
      const configData = response.data.data;
      const customization = normalizeWidgetCustomization(
        configData.customization,
        configData.social_buttons
      );
      this._customizationConfig = customization;

      if (configData.submit_statuses) {
        this._submitStatuses = normalizeWidgetSubmitStatus(
          configData.submit_statuses
        );
      }
    });
  };

  /** Сброс состояния ошибки и повторная инициализация */
  retry = async (): Promise<void> => {
    this._isError = false;
    this._isInitialized = false;
    await this.init();
  };
}
